import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorResponse, ResponseCode } from '@lsq-frontend/angular-shared/api-service';
import { ApiRoutes, UiRouteLinks } from '@lsq-frontend/angular-shared/app-constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { EMPTY, from, Observable, of as observableOf, throwError, timer } from 'rxjs';
import { catchError, delayWhen, map, retryWhen, switchMap, switchMapTo } from 'rxjs/operators';
import { Regex } from '../../constants/regex.constants';
import { ErrorMessageHelper } from '../../utilities/error-message.i18n.helper';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import Bugsnag from '@bugsnag/js';

@Injectable({ providedIn: 'root' })
export class ResponseInterceptor implements HttpInterceptor {
    private _userService: UserService;
    private _errorToastBlacklistService: ErrorToastBlacklistService;
    private _router: Router;
    private _settings: ISettings;
    private _alertService: AlertService;
    _maxRetry: number = 2;

    constructor(
        private _i18n: I18n,
        private _errorMessageHelper: ErrorMessageHelper,
        public injector: Injector
    ) {
        this._errorToastBlacklistService = this.injector.get(ErrorToastBlacklistService);
        this._settings = this.injector.get(SETTINGS);
        this._alertService = this.injector.get(AlertService);
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // These must be injected inside `intercept`, otherwise you'll see a "Maximum call stack size
        // exceeded" error.
        this._userService = this.injector.get(UserService);
        this._router = this.injector.get(Router);

        let retryCount = 0;
        return next.handle(request).pipe(
            map((response: HttpEvent<any>) => {
                const responseBody = (response as HttpResponse<any>).body;

                // Parse the 'jsonResponse' from FinancingProxy.
                if (
                    (request.url.match(new RegExp(`\\/FinancingProxy`)) ||
                        request.url.match(new RegExp(`\\/LighthouseProxy`))) &&
                    responseBody &&
                    responseBody.jsonResponse
                ) {
                    const formattedResponse = JSON.parse(responseBody.jsonResponse);

                    return (response as HttpResponse<any>).clone({
                        body: formattedResponse
                    });
                }

                if (request.url.match(new RegExp(`\/${ApiRoutes.Api}\/`))) {
                    return this._transformDateStringsToDates(response);
                }

                return response;
            }),
            // retry 3 times with increasing duration.
            // reference: https://www.tektutorialshub.com/angular/retry-retrywhen-in-angular-observable/
            retryWhen((errors) =>
                errors.pipe(
                    map((e) => {
                        retryCount++;
                        if (retryCount > this._maxRetry) {
                            // you could add and check request header "SkipRetry" to skip trying from here.
                            throw e;
                        }
                        console.debug(e);
                        console.debug(
                            `Retrying http request ${request.method} to ${request.url} in ${retryCount} seconds.`
                        );
                        Bugsnag.notify(e);
                    }),
                    delayWhen(() => timer(retryCount * 1000))
                )
            ),
            switchMap((response) => {
                if (response instanceof HttpResponse && !!response.body?.error) {
                    return throwError(response.body.error);
                } else if (
                    response instanceof HttpResponse &&
                    (response.body?.errors?.length ?? 0) > 0
                ) {
                    // Handle GraphQL errors
                    console.error(response.body.errors);
                    return throwError(response.body.errors[0]);
                }
                return observableOf(response);
            }),
            catchError((errorResponse: HttpErrorResponse | ErrorResponse) => {
                const error = new ErrorResponse(errorResponse);

                // unauthorizedInterceptorBlacklist has GetCurrentUser, this name is confusing.
                // GetCurrentUser should be actually "whitelisted", so 401 or 403 will not be redirected to login page.
                // Any other API out of the list, like GetSomeOtherApiData, will be redirected to login page when 401 or 403.
                let isWhiteListed =
                    this._settings.unauthorizedInterceptorBlacklist &&
                    this._settings.unauthorizedInterceptorBlacklist.find((item) =>
                        request.url.toLowerCase().endsWith(item.toLowerCase())
                    );
                const isUnauthorized = () => error.code === ResponseCode.Unauthorized;
                const isCsrfError = () =>
                    error.code === ResponseCode.Forbidden &&
                    error.message &&
                    typeof error.message === 'string' &&
                    error.message.indexOf('CSRF') >= 0;

                if (!isWhiteListed) {
                    let bugsnagErrorMessage = `Error on http request ${request.method} ${
                        request.urlWithParams
                    }
                    ${JSON.stringify(error)}
                    ${JSON.stringify(request.body)}`;

                    console.debug(bugsnagErrorMessage);
                    Bugsnag.notify(bugsnagErrorMessage);
                }

                // On 401 or CSRF error, swallow the error and direct to /login.
                // Else, report the error normally.
                // Do not redirect to /login when it's GetCurrentUser when validating current cookie and session.
                if (!isWhiteListed && (isUnauthorized() || isCsrfError())) {
                    console.debug(
                        '[response.interceptor] Redirecting to /login page when 401 or 403 error on a regular API call.'
                    );
                    console.debug(error);
                    this._userService.clearSession(error);
                    return from(this._router.navigateByUrl(UiRouteLinks.Login)).pipe(
                        switchMapTo(EMPTY)
                    );
                } else {
                    if (
                        !this._errorToastBlacklistService.blacklist.find(
                            (item) =>
                                request.url.toLowerCase().endsWith(item.url) &&
                                item.errorCodes.includes(error.code)
                        )
                    ) {
                        // If we aren't explicitly blacklisting this url/code, show the appropriate error toast.
                        if (error.code === ResponseCode.Forbidden) {
                            this._alertService.error(this._errorMessageHelper.PERMISSION_ERROR);
                        } else if (
                            error.code === ResponseCode.Error ||
                            error.code === ResponseCode.InvalidRequest ||
                            error.code === ResponseCode.None
                        ) {
                            // Per product team, show generic error for invalid requests as well as 500s because
                            // most users don't understand technical error messages.
                            this._alertService.error(this._errorMessageHelper.GENERIC_ERROR);
                        }
                    }
                    throw error;
                }
            })
        );
    }

    private _transformDateStringsToDates(response: HttpEvent<any>): HttpEvent<any> {
        if (
            response instanceof HttpResponse &&
            typeof response.body === 'object' &&
            !(response.body instanceof Blob)
        ) {
            return response.clone({
                body: Object.keys(response.body).reduce((newBody, key) => {
                    const currentValue = response.body[key];
                    if (
                        (Regex.ApiDate.test(currentValue) ||
                            key.endsWith('Date') ||
                            key === 'date' ||
                            key.endsWith('At')) &&
                        typeof currentValue === 'string'
                    ) {
                        newBody[key] = new Date(currentValue);
                    } else if (
                        currentValue !== null &&
                        (key.endsWith('Date') || key === 'date' || key.endsWith('At')) &&
                        typeof currentValue === 'object' &&
                        typeof currentValue.year === 'number' &&
                        typeof currentValue.month === 'number' &&
                        typeof currentValue.day === 'number'
                    ) {
                        const { year, month, day } = currentValue;
                        newBody[key] = new Date(year, month - 1, day);
                    } else {
                        newBody[key] = currentValue;
                    }
                    return newBody;
                }, {})
            });
        }
        return response;
    }
}
